import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import { academyCustomCSS } from '~/constants/academyCustomCSS';
import { media, theme } from '~/styles';
import { RichTextContent } from '~/features/rich-text-editor';
import TruncatedDiv from '../../widget/truncated-div';

export default class AcademyGuideOverview extends Component {
  TRANSLATED_COURSE_OUTLINE_HEADING = I18n.t('js.academy.guide_overview.course_outline_heading');

  TRANSLATED_COURSE_OUTLINE_PASSED = I18n.t('js.academy.guide_overview.passed');

  TRANSLATED_COURSE_OUTLINE_FAILED = I18n.t('js.academy.guide_overview.failed');

  TRANSLATED_UNTITLED = I18n.t('js.academy.guide_overview.untitled');

  render() {
    const { academyOverviewDescription } = this.props.guide;

    return (
      <AcademyGuideOverviewWrapper>
        {academyOverviewDescription && (
          <AcademyContentOverviewDescriptionWrapper>
            <RichTextContent sanitizeOptions={{ allowSafeTags: true, allowedDangerousTags: ['iframe'] }}>
              {this.props.guide.academyOverviewDescription}
            </RichTextContent>
          </AcademyContentOverviewDescriptionWrapper>
        )}
        {this.renderTable()}
      </AcademyGuideOverviewWrapper>
    );
  }

  renderPassFailIndicator = (didPassAssignment) => (
    <PassFailIndicator>
      {didPassAssignment ? (
        <PassFailIndicatorImg src="/images/v2/confirm-icon-filled-green.svg" />
      ) : (
        <PassFailIndicatorImg src="/images/v2/reject-icon-filled-red.svg" />
      )}
      <PassFailIndicatorText>
        {didPassAssignment ? this.TRANSLATED_COURSE_OUTLINE_PASSED : this.TRANSLATED_COURSE_OUTLINE_FAILED}
      </PassFailIndicatorText>
    </PassFailIndicator>
  );

  renderTable = () => {
    let shouldShowPassFail = false;
    let didPassAssignment;
    const { assignment } = this.props;
    if (assignment) {
      const { course, passStatusMetadata } = assignment;
      const { didPass, didFail } = passStatusMetadata;
      didPassAssignment = didPass;
      shouldShowPassFail = course && course.requirePassingScore && (didPass || didFail);
    }

    return (
      <OutlineWrapper
        className={academyCustomCSS.overview.guideOverviewOutlineWrapper}
        hasOverviewDescription={this.props.guide.academyOverviewDescription}
      >
        <HeaderWrapper className={academyCustomCSS.overview.guideOverviewHeaderContainer}>
          <Header className={academyCustomCSS.overview.guideOverviewTitle}>
            {this.TRANSLATED_COURSE_OUTLINE_HEADING}
          </Header>
          {shouldShowPassFail && this.renderPassFailIndicator(didPassAssignment)}
        </HeaderWrapper>
        <TableWrapper className={academyCustomCSS.overview.guideOverviewSectionContainer} role="list">
          {_.map(
            _.sortBy(this.props.guide.courseSections, (section) => section.order),
            (section) => this.renderSection(section),
          )}
        </TableWrapper>
      </OutlineWrapper>
    );
  };

  renderSection = (section) => (
    <SectionWrapper key={section.id} role="listitem">
      <SectionHeader className={academyCustomCSS.overview.guideOverviewSectionTitle}>
        <SectionTitle role="heading" aria-level="4">
          <TruncatedDiv text={section.title} numLines={2} />
        </SectionTitle>
      </SectionHeader>
      <SectionBody className={academyCustomCSS.overview.guideOverviewSectionTasksContainer} role="list">
        {_.map(
          _.sortBy(section.courseSectionTasks, (task) => task.order),
          (task) => this.renderTask(task),
        )}
      </SectionBody>
    </SectionWrapper>
  );

  renderTask = (task) => {
    const { assignment, academyContent, certificationId, customPageSlug, userNeedsToPurchaseContent } = this.props;

    const taskTitle = <TaskTitle>{task.title || this.TRANSLATED_UNTITLED}</TaskTitle>;

    let taskLink;
    // TODO - (https://workramp.atlassian.net/browse/PAR-895) Emergency hack for now - look into doing this better after hotfix
    const taskLinkTrainingId = academyContent.objectId || academyContent.id;
    if (assignment) {
      taskLink = `/guides/${taskLinkTrainingId}/tasks/${task.id}`;
    } else {
      taskLink = `/training/${taskLinkTrainingId}/register`;

      if (certificationId) {
        taskLink = `${taskLink}?certification_id=${certificationId}`;
      }
    }

    const slugPrefix = customPageSlug ? `/pages/${customPageSlug}` : '';
    taskLink = `${slugPrefix}${taskLink}`;

    return (
      <TaskWrapper key={task.id} role="listitem">
        {this.renderTaskCompletionStatusDot(task)}
        {userNeedsToPurchaseContent ? (
          <div className={academyCustomCSS.overview.guideOverviewSectionTaskTitle}>{taskTitle}</div>
        ) : (
          <a className={academyCustomCSS.overview.guideOverviewSectionTaskTitle} href={taskLink}>
            {taskTitle}
          </a>
        )}
      </TaskWrapper>
    );
  };

  renderTaskCompletionStatusDot = (task) => {
    const isComplete = this.props.assignment && this.props.assignment.completedTaskIds.includes(task.id);
    const accentColor = this.props.themeOptions.accentColor || '#126ED1';

    return (
      <TaskCompletionStatusDot
        isComplete={isComplete}
        accentColor={accentColor}
        className={academyCustomCSS.overview.completionStatusDot}
      />
    );
  };
}

const AcademyGuideOverviewWrapper = styled.div`
  flex-basis: 100%;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const OutlineWrapper = styled.div`
  flex-basis: 100%;
  ${(props) => props.hasOverviewDescription && `flex-basis: 48%;`}
  ${media.mobile} {
    flex-basis: 100%;
  }
  h2:first-child {
    margin-top: 0;
  }
`;

const TableWrapper = styled.div`
  border-left: 1px solid #cbcbcb;
  border-right: 1px solid #cbcbcb;
  border-bottom: 1px solid #cbcbcb;
`;

const SectionWrapper = styled.div``;

const SectionHeader = styled.div`
  height: 60px;
  background-color: #f1f1f1;
  border-top: 1px solid #cbcbcb;
  border-bottom: 1px solid #cbcbcb;
  padding-left: 35px;
  padding-right: 35px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.div`
  color: ${theme.color.textPrimary};
  font-size: 1.25rem;
  ${theme.font.semibold}
  margin-right: 2rem;
`;

const SectionBody = styled.div`
  box-sizing: border-box;
  overflow: auto;
  height: auto;
  transition:
    padding-top 0.1s linear,
    padding-bottom 0.1s linear;
  padding: 20px 35px;
`;

const TaskWrapper = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  &:last-child {
    margin-bottom: 0;
  }
  a {
    color: #555 !important;
  }
`;

const TaskTitle = styled.div``;

const TaskCompletionStatusDot = styled.div`
  box-sizing: border-box;
  height: 13px;
  width: 13px;
  background-color: ${(props) => (props.isComplete ? props.accentColor : 'transparent')};
  border-radius: 50%;
  border: 1px solid ${(props) => (props.isComplete ? props.accentColor : '#979797')};
  margin-right: 1.5rem;
  flex: none;
`;

const AcademyContentOverviewDescriptionWrapper = styled.div`
  flex-basis: 48%;
  padding: 0 !important;

  *:first-child {
    margin-top: 0;
  }
  *:last-child {
    margin-bottom: 0;
  }

  iframe {
    width: 100% !important;
    max-width: 100% !important;
  }

  ${media.mobile} {
    flex-basis: 100%;
    margin-bottom: 50px;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  margin-bottom: 1.2rem;
`;

const Header = styled.h2`
  margin-bottom: 0;
  margin-right: 8px;
`;

const PassFailIndicator = styled.div`
  display: flex;
  align-items: center;
`;

const PassFailIndicatorImg = styled.img`
  height: 16px;
  width: 16px;
  margin-right: 4px;
`;

const PassFailIndicatorText = styled.span`
  font-size: 0.875rem;
`;
